import React, { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';

/**
 * PWAUpdateHelper listens for service worker updates and prompts the user
 * to refresh the page to get the latest version.
 */
export const PWAUpdateHelper: React.FC = () => {
    const { t } = useTranslation();
    const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
    const [showUpdateBar, setShowUpdateBar] = useState(false);

    useEffect(() => {
        if (!('serviceWorker' in navigator)) return;

        // Helper to handle a new service worker
        const onNewWorker = (worker: ServiceWorker) => {
            setWaitingWorker(worker);
            setShowUpdateBar(true);
        };

        // Check for updates every hour
        const checkInterval = setInterval(() => {
            navigator.serviceWorker.getRegistration().then(reg => {
                if (reg) reg.update();
            });
        }, 1000 * 60 * 60);

        // Initial check
        navigator.serviceWorker.getRegistration().then(registration => {
            if (!registration) return;

            // 1. If there's already a waiting worker
            if (registration.waiting) {
                onNewWorker(registration.waiting);
            }

            // 2. Listen for new workers being installed
            registration.addEventListener('updatefound', () => {
                const newWorker = registration.installing;
                if (!newWorker) return;

                newWorker.addEventListener('statechange', () => {
                    if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                        onNewWorker(newWorker);
                    }
                });
            });
        });

        // 3. Listen for controlling change (reload after skipWaiting)
        let refreshing = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
            if (refreshing) return;
            refreshing = true;
            window.location.reload();
        });

        return () => clearInterval(checkInterval);
    }, []);

    const reloadAndActivate = (worker: ServiceWorker | null) => {
        const sw = worker || waitingWorker;
        if (sw) {
            sw.postMessage({ type: 'SKIP_WAITING' });
        } else {
            window.location.reload();
        }
    };

    if (!showUpdateBar) return null;

    return (
        <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-8 md:w-80 z-[100] animate-in fade-in slide-in-from-bottom-5">
            <div className="bg-[#4a7c5e] text-white p-4 rounded-xl shadow-2xl border border-white/20 flex flex-col gap-3 backdrop-blur-md">
                <div className="flex items-center gap-2 font-semibold">
                    <RefreshCw className="w-5 h-5 animate-spin-slow" />
                    <span>{t('pwa.update.title')}</span>
                </div>
                <p className="text-sm opacity-90">
                    {t('pwa.update.desc')}
                </p>
                <Button
                    onClick={() => reloadAndActivate(waitingWorker)}
                    className="bg-white text-[#4a7c5e] hover:bg-gray-100 font-bold py-2 rounded-lg transition-all active:scale-95"
                >
                    {t('pwa.update.cta')}
                </Button>
            </div>
        </div>
    );
};

export default PWAUpdateHelper;
